/**
 * DashboardPage — container for the practitioner home (`/dashboard`).
 *
 * Reads the logged-in practitioner from the auth store to build the greeting
 * and to seed the phone-call note's `practitionerId` (Flutter
 * `dashboard_screen.dart` seeds `practitionerId: userData.id`). Reuses the
 * shipped Today worklist (`TodaysAppointmentsPage` → `todays-appointments-view`)
 * as the home body, and mounts the phone-call capture drawer.
 */
import { useEffect } from 'react';
import { useAuthStore } from '@/auth/auth-store';
import { isDoctor } from '@/auth/roles';
import { useReadyList } from '@/shared/lib/use-ready-list';
import { TodaysAppointmentsPage } from '@/scheduling/todays-appointments-page';
import { DashboardView } from './dashboard-view';
import { PhoneCallPanel } from './phone-call-panel';
import { usePhoneCallDrawerStore } from './store/phone-call-drawer-store';
import { ReadyListHome, ReadyListCountLine } from './ready-list/ready-list-home';
import { homeGreetingName } from './ready-list/ready-list-data';
import { practiceNow } from '@/shared/lib/practice-time';

/** Time-of-day greeting word (local time). */
function greetingWord(now: Date): string {
  const h = now.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

/**
 * "Firstname Lastname" from the auth user, falling back gracefully.
 *
 * No honorific is prepended: `/dashboard` is open to every authenticated
 * persona (nurse, technician, coach, nutritionist, …), and `UserData` carries
 * no profile-provided title. Hardcoding "Dr." would mislabel non-doctors, and a
 * role→honorific matrix would be a hardcoded role table (RBAC is data, not a
 * role map). So the greeting uses the plain name.
 */
function practitionerName(firstName?: string, lastName?: string): string {
  return [firstName, lastName].filter(Boolean).join(' ').trim();
}

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  // The ready-list home (on by default since 15 Oct; `?x=-ready-list` turns it off): one Today screen for every practitioner (Samer,
  // 4 Nov). A nurse's list is the providers she supports (useReadyListData follows the worklist's provider scope).
  const readyList = useReadyList();
  const showReadyList = readyList;
  // Lives in a shell-shared store, not local state: AppShell (a sibling of
  // this routed page) needs to read it too, to `inert` the persistent nav
  // rail while the drawer is open — see phone-call-drawer-store.ts.
  const phoneCallOpen = usePhoneCallDrawerStore((s) => s.open);
  const setPhoneCallOpen = usePhoneCallDrawerStore((s) => s.setOpen);

  // Safety net for the shared store: if this page unmounts by any path other
  // than the drawer's own close (e.g. a direct route change while the drawer
  // was open), clear the flag so AppShell doesn't leave the nav rail
  // permanently `inert` on the next route.
  useEffect(() => {
    return () => setPhoneCallOpen(false);
  }, [setPhoneCallOpen]);

  const now = practiceNow();
  // Ready list: "Dr <last name>" for a physician. OFF (and a nurse): the plain name, as before.
  const name = showReadyList
    ? homeGreetingName(user?.firstName, user?.lastName, isDoctor(user))
    : practitionerName(user?.firstName, user?.lastName);
  const greeting = name ? `${greetingWord(now)}, ${name}` : greetingWord(now);
  const dateLabel = now.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  // Flutter seeds the phone-call `practitionerId` from `userData.id`. Leave it
  // `undefined` (NOT '') when the user carries no id — the backend DTO's
  // `PractitionerId` is a non-nullable Guid, so the panel disables the save
  // rather than POSTing an empty/zero Guid that would 400.
  const practitionerId = user?.practitionerId ?? user?.id ?? user?.userId;

  return (
    <DashboardView
      greeting={greeting}
      dateLabel={showReadyList ? <ReadyListCountLine fallback={dateLabel} /> : dateLabel}
      hidePhoneCall={showReadyList}
      worklist={showReadyList ? <ReadyListHome /> : <TodaysAppointmentsPage phoneCall={false} hidePracticePicker />}
      phoneCallOpen={phoneCallOpen}
      onOpenPhoneCall={() => setPhoneCallOpen(true)}
      onClosePhoneCall={() => setPhoneCallOpen(false)}
      phoneCallPanel={
        phoneCallOpen ? (
          <PhoneCallPanel
            practitionerId={practitionerId}
            onClose={() => setPhoneCallOpen(false)}
          />
        ) : null
      }
    />
  );
}
